/**
 * Pan, zoom and measurement on a map image. PURE — no DOM, no canvas.
 *
 * Three coordinate spaces meet here, and conflating any two of them produces something
 * that looks plausible and is wrong:
 *
 *   CANVAS px   where the mouse is, and where things are drawn. Changes with zoom.
 *   IMAGE px    a fixed position in the map PNG. What `map_points_v2` reports — its
 *               `positionX`/`positionY` are IMAGE PIXELS, not metres, confirmed against
 *               the live C40 (a charging pile at 339.99, 241.95 on a 795x627 map; at
 *               0.05 m/px a 40 m-wide map cannot have a point at x = 340 m).
 *   WORLD m     where the robot says it is. `telemetry/localization`, via the map's
 *               origin and resolution.
 *
 * So a map point and a robot pose arrive in DIFFERENT SPACES from the same vendor, and
 * the conversion between them is the thing this file exists to keep honest.
 */
import type { MapFrame } from "./mapFrame";

export interface View {
  /** Canvas pixels per image pixel. */
  scale: number;
  /** Canvas position of image pixel (0, 0). */
  x: number;
  y: number;
}

/** The view that fits an image into a canvas, centred. */
export function fitView(imgW: number, imgH: number, canvasW: number, canvasH: number): View {
  const scale = Math.min(canvasW / imgW, canvasH / imgH);
  return { scale, x: (canvasW - imgW * scale) / 2, y: (canvasH - imgH * scale) / 2 };
}

export const toCanvas = (v: View, ix: number, iy: number) =>
  ({ x: v.x + ix * v.scale, y: v.y + iy * v.scale });

export const toImage = (v: View, cx: number, cy: number) =>
  ({ ix: (cx - v.x) / v.scale, iy: (cy - v.y) / v.scale });

/**
 * Zoom about a fixed canvas point — the cursor.
 *
 * The image pixel under the cursor must stay under the cursor, or zooming walks the map
 * away from whatever you were looking at, which is the single thing that makes a zoom
 * feel broken.
 */
export function zoomAt(v: View, cx: number, cy: number, factor: number,
                       min = 0.05, max = 40): View {
  const scale = Math.min(max, Math.max(min, v.scale * factor));
  const before = toImage(v, cx, cy);
  // Solve for the offset that keeps `before` under (cx, cy) at the new scale.
  return { scale, x: cx - before.ix * scale, y: cy - before.iy * scale };
}

export const panBy = (v: View, dx: number, dy: number): View =>
  ({ ...v, x: v.x + dx, y: v.y + dy });

// ---- image pixels <-> world metres --------------------------------------------
//
// NOT HERE. `mapFrame` owns that transform — `worldToPixel` and `pixelToWorld`. This
// file had its own copy, which was harmless until the convention turned out to be wrong,
// at which point two homes for one rule meant two places to fix and one to forget.
//
// They are not re-exported under other names either. `worldToImage`/`imageToWorld`
// returning `{ix, iy}` while `worldToPixel` returned `{px, py}` was two vocabularies for
// one pair of numbers, and the aliases hid a shape mismatch that the typechecker never
// got to see, because the test run that failed first also stopped the build.

/**
 * The distance between two IMAGE pixels, in both spaces.
 *
 * Metres are computed per axis before combining, because the two axes may not share a
 * scale — squareness is an assumption here, not a fact. Multiplying a pixel distance by
 * one resolution would silently assume it.
 */
export function measure(f: MapFrame | null, a: { ix: number; iy: number },
                        b: { ix: number; iy: number }) {
  const dxPx = b.ix - a.ix;
  const dyPx = b.iy - a.iy;
  const px = Math.hypot(dxPx, dyPx);
  if (!f) return { px, m: null as number | null, dxPx, dyPx };
  return { px, m: Math.hypot(dxPx * f.resX, dyPx * f.resY), dxPx, dyPx };
}
